import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, NoteBlock, TableBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import { offeringControls } from './offeringcontrols'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside } from './writes'

/**
 * Providers › Offerings & buy prices (`pv-offerings`), from three reads.
 *
 *   GET /v1/admin/offerings                     the routing table
 *   GET /v1/admin/offerings/{key}/versions      one offering's history
 *   GET /v1/admin/offerings/{key}/buy-prices    what it costs us
 *
 * # Until now this table was configurable only by hand-written SQL
 *
 * Which institution carries which rail, in which direction, in which currency,
 * between which limits and up to which cutoff — and the only way to change it
 * was a statement typed at a database prompt, which *"writes no audit row
 * (invariant 6), leaves a live interval nobody closed (invariant 9), and is
 * refused outright by migration 101's deferred cutoff trigger."*
 *
 * # The tariff is a SEPARATE read, and this console must not undo that
 *
 * `offering.read` and `offering.write` are Ops. The buy price is Finance's,
 * *"because what an institution charges US is a transcription of a tariff annex
 * rather than a decision anybody here makes"*. The backend states the mistake
 * to avoid in terms:
 *
 * > *"what this file must not do is undo them by folding a cost figure into the
 * > routing read, which would hand every institution's tariff to Ops and, by
 * > inheritance, to Compliance as a side effect of a JOIN."*
 *
 * So there is **no cost column on the list** — the corpus screen had one — and
 * the buy-price history is a second request on the row opener. An Ops session
 * gets that request refused, and the refusal renders as a sentence naming
 * Finance rather than as an error or as an empty table. An empty buy-price
 * table would read as *no buy price*, which is a statement about the tariff
 * this session is not entitled to make.
 *
 * # `live` and `available` are two questions and both are needed
 *
 * *"A live version that is switched off is amended back on, and a closed one
 * has no terms at all and is reinstated."* Collapsing them into one "state"
 * chip would send an operator to the wrong control.
 *
 * # An empty version list is an answer, and it is which control to use
 *
 * The versions read is `200` with `[]` for a key with no versions, never `404`,
 * *"and the empty list is what an operator resolves the write path's two
 * refusals against: no versions at all is Define's case, versions with none
 * live is Reinstate's."* This module renders that as the sentence, not as a
 * blank.
 *
 * # No buy price does NOT mean free
 *
 * *"The live buy price is closed and nothing succeeds it. This offering now has
 * no cost governing: every reader must treat that as unknown rather than as
 * free."* An offering between contracts is a real, honest state, and rendering
 * it as zero would put a wrong margin in a report.
 */

export const OFFERINGS_PATH = '/offerings'

export function offeringVersionsPath(key: string): string {
  return `/offerings/${encodeURIComponent(key)}/versions`
}

export function offeringBuyPricesPath(key: string): string {
  return `/offerings/${encodeURIComponent(key)}/buy-prices`
}

const SOURCE = 'postgres · provider_method_offerings (GET /v1/admin/offerings)'
const AUTHORITY = 'operational'

/** The newest version's terms, as they appear on a listing row. */
interface Terms {
  readonly version: number | null
  readonly versionId: number | null
  /** `effective_to IS NULL`. Not the same question as `available`. */
  readonly live: boolean | null
  /** The switch. A live version that is off is amended back on. */
  readonly available: boolean | null
  readonly currency: string
  readonly minAmountMinor: number | null
  /** Absent is UNBOUNDED, which is not a ceiling of zero. */
  readonly maxAmountMinor: number | null
  readonly timezone: string
  readonly runsOnWeekdays: number | null
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly createdBy: string
}

interface Offering {
  readonly key: string
  readonly provider: string
  readonly method: string
  readonly direction: string
  /** The IDENTITY's currency. It survives a retirement. */
  readonly currency: string
  readonly versions: number | null
  /** Null when the identity carries no version at all. */
  readonly newest: Terms | null
}

function readTerms(raw: unknown): Terms | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  return {
    version: int(o['version']),
    versionId: int(o['version_id']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
    available: typeof o['available'] === 'boolean' ? o['available'] : null,
    currency: str(o['currency']),
    minAmountMinor: int(o['min_amount_minor']),
    maxAmountMinor: int(o['max_amount_minor']),
    timezone: str(o['cutoff_timezone']),
    runsOnWeekdays: int(o['runs_on_weekdays']),
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    createdBy: str(o['created_by']),
  }
}

function readOffering(raw: unknown): Offering | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const key = str(o['offering_key'])
  if (key === '') return null
  return {
    key,
    provider: str(o['provider_key']),
    method: str(o['method_code']),
    direction: str(o['direction']),
    currency: str(o['currency']),
    versions: int(o['versions']),
    newest: readTerms(o['newest']),
  }
}

/**
 * The columns, and the one that must never be here.
 *
 * There is no buy price. The list is Ops', the tariff is Finance's, and a cost
 * column would hand every institution's tariff to Ops as a side effect of the
 * routing read. The corpus screen had one.
 */
const COLS: readonly Column[] = [
  ['Offering', 'm', 'minmax(180px,1.3fr)'],
  ['Institution', 'm', '120px'],
  ['Method', 'm', '130px'],
  ['Dir', 'c', '100px'],
  ['Version', 'm', '110px'],
  ['Offered', 'c', '150px'],
  ['Band', 'a', 'minmax(180px,1.3fr)'],
  ['Runs on', 'm', '120px'],
  ['Cutoffs in', 'm', '150px'],
]

function directionCell(d: string): ChipCell {
  switch (d) {
    case 'in': return ['inbound', 'n']
    case 'out': return ['outbound', 'n']
    case '': return ['— not stated', 'r']
    // `both` is a payment METHOD's capability and never an offering's: the
    // limits, cutoff and buy price of an inbound credit have nothing to do with
    // those of an outbound payment, so a rail carried both ways is two.
    default: return [`${d} — not an offering direction`, 'r']
  }
}

/**
 * Whether the rail is offered, from BOTH flags.
 *
 * `live` and `available` are different questions and the pair decides which
 * control an operator needs. Collapsing them would send somebody to amend a
 * retired offering, which is the one thing amend refuses.
 */
function offeredCell(t: Terms | null): ChipCell {
  if (t === null) return ['no version at all', 'r']
  if (t.live === null || t.available === null) return ['— not stated', 'r']
  if (!t.live) return ['retired — reinstate it', 'n']
  return t.available ? ['offered', 'g'] : ['live but switched off', 'a']
}

/**
 * The amount band, with its currency, in minor units.
 *
 * An absent maximum is UNBOUNDED and says so — a ceiling of zero and no ceiling
 * are opposite statements, and the domain refuses a zero ceiling so the two can
 * never be confused downstream either. A band with no currency is not drawn:
 * law 8, and the backend never sends one.
 */
function bandCell(t: Terms | null): Cell {
  if (t === null || t.minAmountMinor === null) return '—'
  if (t.currency === '') return '— an amount arrived with NO CURRENCY and is not shown'
  const min = `${String(t.minAmountMinor)} minor`
  const max = t.maxAmountMinor === null ? 'no ceiling' : `${String(t.maxAmountMinor)} minor`
  return `${min} → ${max} · ${t.currency}`
}

/**
 * How many weekdays the version runs on.
 *
 * ZERO is the shape of a withdrawn rail, and it is a real answer rather than a
 * missing one — so it is spelled out. A blank cell here would read as "the
 * schedule was not returned".
 */
function runsOnCell(t: Terms | null): Cell {
  if (t === null || t.runsOnWeekdays === null) return '—'
  if (t.runsOnWeekdays === 0) return 'NO weekday — it runs never'
  return `${String(t.runsOnWeekdays)} of 7 days`
}

function row(o: Offering): readonly Cell[] {
  const t = o.newest
  return [
    o.key,
    o.provider === '' ? '— not stated' : o.provider,
    o.method === '' ? '— not stated' : o.method,
    directionCell(o.direction),
    t === null || t.version === null
      ? `${o.versions === null ? '0' : String(o.versions)} versions`
      : `v${String(t.version)} of ${String(o.versions ?? t.version)}`,
    offeredCell(t),
    bandCell(t),
    runsOnCell(t),
    // IANA and never an offset: an offset is wrong twice a year, and a cutoff
    // that moves an hour books payments to the wrong value date.
    t === null || t.timezone === '' ? '—' : t.timezone,
  ]
}

export function composeOfferings(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['offerings'])
    ? (payload['offerings'] as readonly unknown[])
    : []
  const rows: Offering[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readOffering(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const versionless = rows.filter((o) => o.newest === null).length
  const dark = rows.filter((o) => o.newest?.runsOnWeekdays === 0).length

  const table: TableBlock = {
    t: 'table',
    title: 'The routing table',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} offering(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'Every offering, live and retired, in one list — and with NO cost figure on any row. What an institution charges us is Finance’s and sits behind its own permission on its own path; folding it in here would hand every tariff to Ops as a side effect of reading the routing table. Open a row for one offering’s history.',
  }

  const blocks: Block[] = [table]

  if (versionless > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'An identity with no terms',
      title: `${String(versionless)} offering(s) carry no version at all`,
      body: 'That is a state this console’s controls cannot produce and hand-written SQL can. An identity with no version has no limits, no cutoff and no schedule — routing has nothing to resolve against it. It is shown rather than hidden, because the row exists and something has to explain why nothing else about it does.',
      ref: 'newest is null on those rows',
    })
  }

  if (dark > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Runs on no weekday',
      title: `${String(dark)} offering(s) have a live version that runs on no day of the week`,
      body: 'That is what a withdrawn rail looks like while its terms are still on the books, and it is a legitimate configuration rather than a fault. It is worth seeing because the offering reads as live everywhere else: routing will consider it and find no day on which it can settle.',
      ref: 'runs_on_weekdays is 0',
    })
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} offering row(s) could not be read`,
      body: 'Counted rather than dropped silently. A routing table quietly one rail shorter than the record is one an operator reads as having fewer options than it has, which is how a payment gets attributed to the wrong institution.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Empty',
      title: 'No offering is configured, and routing has nothing to choose between',
      body: 'Every payment will fail compatibility. This is not a neutral empty state: an offering is what says a rail exists at all, so an empty routing table is a deployment that cannot move money in either direction.',
      ref: 'an empty routing table is an outage of capability',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  // Always. Deliberately absent rather than unavailable, and naming where it is
  // served is more use than a column an Ops session would see empty.
  missing.push('what each rail costs us — Finance’s, on its own path per offering')
  missing.push('which offering routing would actually choose — no admin read serves a routing pin or a resolution trace')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    subject: `provider_method_offerings · ${String(rows.length)} offering(s) read`,
    // What a row IS, so opening one reads that offering's own history.
    rowKeys: rows.map((o) => o.key),
  }
}

/** The list, plus the six controls. */
export const loadOfferings: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(OFFERINGS_PATH)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The offerings endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The offerings endpoint returned a shape this console does not know.', null)
  }

  const caller = await callerBeside(api)
  const data = composeOfferings(payload as Record<string, unknown>, new Date())
  return { ...data, blocks: [...data.blocks, ...offeringControls(api, caller)] }
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The routing table could not be read',
      body: `${reason} Nothing is drawn, and no control is offered over rails this console cannot see. This is NOT an empty routing table: whether any rail is configured is unknown from here, and an empty one would mean no payment can be made at all.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

// ---------------------------------------------------------------------------
// One offering, opened from a row
// ---------------------------------------------------------------------------

const VERSION_COLS: readonly Column[] = [
  ['Version', 'm', '100px'],
  ['Offered', 'c', '150px'],
  ['Band', 'a', 'minmax(180px,1.3fr)'],
  ['The week it ran', 'x', 'minmax(240px,1.8fr)'],
  ['Zone', 'm', '140px'],
  ['From', 'm', '116px'],
  ['To', 'm', '116px'],
  ['Written by', 'x', 'minmax(150px,1.1fr)'],
]

const PRICE_COLS: readonly Column[] = [
  ['Version', 'm', '100px'],
  ['In force', 'c', '110px'],
  ['Flat', 'a', '140px'],
  ['Rate', 'n', '110px'],
  ['Floor', 'a', '130px'],
  ['Cap', 'a', '130px'],
  ['Rounding', 'm', '120px'],
  ['Transcribed from', 'x', 'minmax(200px,1.5fr)'],
  ['From', 'm', '116px'],
]

/**
 * One version's whole week, drawn from the list the backend sent.
 *
 * A weekday ABSENT from the list is a day the offering did not run — the
 * fail-closed encoding the domain uses, preserved on the wire *"rather than
 * expanded into seven nullable slots where 'no opinion recorded' and 'open all
 * day' would look the same."* So an empty list is rendered as the sentence, not
 * as a blank.
 */
function weekCell(raw: unknown): Cell {
  if (!Array.isArray(raw)) return '— not returned'
  const parts: string[] = []
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue
    const c = entry as Record<string, unknown>
    const day = str(c['weekday'])
    const at = str(c['cutoff_local'])
    const lag = int(c['settlement_lag_days'])
    if (day === '') continue
    parts.push(`${day.slice(0, 3)} ${at === '' ? '??:??' : at}${lag === null ? '' : ` +${String(lag)}d`}`)
  }
  return parts.length === 0 ? 'no weekday at all — it ran never' : parts.join(' · ')
}

function amount(minor: number | null, currency: string, absent: string): Cell {
  if (minor === null) return absent
  if (currency === '') return '— no currency'
  return `${String(minor)} minor · ${currency}`
}

function versionRow(raw: unknown): readonly Cell[] | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const version = int(o['version'])
  if (version === null) return null
  const t = readTerms(raw)
  return [
    `v${String(version)}`,
    offeredCell(t),
    bandCell(t),
    weekCell(o['cutoffs']),
    str(o['cutoff_timezone']) === '' ? '—' : str(o['cutoff_timezone']),
    str(o['effective_from']) === '' ? '—' : day(str(o['effective_from'])),
    str(o['effective_to']) === '' ? 'open' : day(str(o['effective_to'])),
    str(o['created_by']) === '' ? '— unattributed' : str(o['created_by']),
  ]
}

function priceRow(raw: unknown): readonly Cell[] | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const version = int(o['version'])
  if (version === null) return null
  const currency = str(o['currency'])
  const live = typeof o['live'] === 'boolean' ? o['live'] : null
  const bps = int(o['percent_bps'])
  return [
    `v${String(version)}`,
    live === null
      ? (['— not stated', 'r'] satisfies ChipCell)
      : live
        ? (['in force', 'g'] satisfies ChipCell)
        : (['closed', 'n'] satisfies ChipCell),
    amount(int(o['flat_minor']), currency, '— not stated'),
    bps === null ? '— not stated' : `${String(bps)} bps`,
    // Absent is UNBOUNDED in that direction, which is not a floor of zero.
    amount(int(o['min_cost_minor']), currency, 'no floor'),
    amount(int(o['max_cost_minor']), currency, 'no cap'),
    str(o['rounding']) === '' ? '— not stated' : str(o['rounding']),
    // The whole reason this read exists rather than a report being trusted.
    str(o['source_reference']) === '' ? '— NOTHING TO DEFEND IT' : str(o['source_reference']),
    str(o['effective_from']) === '' ? '—' : day(str(o['effective_from'])),
  ]
}

/**
 * One offering's history: its versions, and its tariff if this session may see
 * one.
 *
 * The two reads are separate requests under separate permissions, and the
 * refusal of the second is rendered as a SENTENCE rather than as an error or an
 * empty table. An empty buy-price table would read as *no buy price*, which is
 * a claim about the tariff a session without `offering.buy_price.read` is not
 * entitled to make — and "no cost governing" is a real and consequential state
 * that must not be manufactured by a permission boundary.
 */
export async function readOfferingDetail(api: ApiClient, key: string): Promise<ScreenData> {
  if (key === '') {
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Not read',
        title: 'No offering was named, so nothing was looked up',
        body: 'A row opens into its own history by its key, and none was given.',
        ref: 'no offering key',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: 'no offering key was given' },
    }
  }

  let priceRefusal: ApiError | null = null
  const versions = await api.get<unknown>(offeringVersionsPath(key)).catch((error: unknown) => {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  })
  const prices = await api.get<unknown>(offeringBuyPricesPath(key)).catch((error: unknown) => {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    if (error instanceof ApiError) priceRefusal = error
    return null
  })

  const versionRows = rowsOf(versions, 'versions', versionRow)
  const priceRows = rowsOf(prices, 'buy_prices', priceRow)

  const blocks: Block[] = [{
    t: 'table',
    title: `${key} — every version, in order`,
    src: 'ledger',
    facets: false,
    cols: VERSION_COLS,
    rows: versionRows,
    footer: versions === null
      ? 'the version history did not answer'
      : `${String(versionRows.length)} version(s)`,
    foot2:
      'Nothing here was ever edited. A retirement closes the live version and a reinstatement opens the next number — never the retired one again and never 1 — so a payment accepted under v2 still resolves to v2 for ever.',
  }]

  if (versions !== null && versionRows.length === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'No versions',
      title: 'This key has no version at all, which is the Define case rather than the Reinstate one',
      body: 'An empty history is answered deliberately rather than as a 404, because it is what tells an operator which control applies: no versions at all means define it; versions with none live means reinstate it. Those are two different acts and two different audit events.',
      ref: 'versions is an empty list',
    })
  }

  blocks.push(tariffBlock(prices, priceRows, priceRefusal, key))

  if (prices !== null && priceRows.length === 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'No cost governing',
      title: 'This offering has no buy price, and that means UNKNOWN — never free',
      body: 'An offering between contracts is a real and honest state: the previous tariff was closed and the replacement has not arrived. Every margin figure that touches this rail has to say the cost is unrecorded. Reading it as zero would put a wrong margin in a report that nothing else would catch.',
      ref: 'buy_prices is an empty list',
    })
  }

  const missing: string[] = []
  if (versions === null) missing.push('the version history')
  if (prices === null) {
    missing.push(priceRefusal === null
      ? 'the buy-price history'
      : 'what this rail costs us — refused, and that is the permission split working')
  }

  return {
    blocks,
    provenance: {
      kind: 'partial',
      source: `postgres · offering versions and buy prices (GET ${offeringVersionsPath(key)})`,
      authority: AUTHORITY,
      readAt: readAtStamp(new Date()),
      missing,
    },
    subject: `${key} · ${String(versionRows.length)} version(s)`,
  }
}

/**
 * The tariff table, or the sentence that stands in for it.
 *
 * A refusal here is the permission split doing its job, not a fault: Ops holds
 * the routing table and Finance holds what it costs. Rendering it as an error
 * would train an operator to report a working boundary as a bug.
 */
function tariffBlock(
  prices: unknown, rows: readonly (readonly Cell[])[], refusal: ApiError | null, key: string,
): Block {
  if (prices !== null) {
    return {
      t: 'table',
      title: `${key} — what it costs us`,
      src: 'ledger',
      facets: false,
      cols: PRICE_COLS,
      rows,
      footer: `${String(rows.length)} buy-price version(s)`,
      foot2:
        'A published buy price is never edited. A correction is a new version, and the one it replaced stays — because a transaction priced under it has to keep resolving.',
    } satisfies TableBlock
  }

  if (refusal !== null && (refusal.status === 403 || refusal.status === 401)) {
    return {
      t: 'note',
      tone: 'n',
      kicker: 'Not yours to see, and that is deliberate',
      title: 'This session may read the routing table but not what the rails cost us',
      body: 'What an institution charges us is Finance’s: it is a transcription of a tariff annex rather than a decision anybody here makes, and it sits behind its own permission on its own path so that reading the routing table cannot hand it over as a side effect. This is the boundary working rather than a fault, and there is deliberately no empty table below — that would read as "no buy price", which is a claim about the tariff this session is not in a position to make.',
      ref: refusal.decisionRef === null
        ? 'offering.buy_price.read — Finance'
        : `decision_ref ${refusal.decisionRef} · offering.buy_price.read — Finance`,
    } satisfies NoteBlock
  }

  return {
    t: 'note',
    tone: 'r',
    kicker: 'Not read',
    title: 'The buy-price history did not answer',
    body: 'Nothing is drawn where the tariff would be. This is NOT the same as an offering with no cost governing, which is a real state that has to be reported as unknown — this is a read that failed, and what this rail costs is simply not known from here.',
    ref: refusal?.decisionRef === null || refusal === null
      ? 'the buy-price read failed'
      : `decision_ref ${refusal.decisionRef}`,
  } satisfies NoteBlock
}

function rowsOf(
  payload: unknown, field: string, build: (raw: unknown) => readonly Cell[] | null,
): readonly (readonly Cell[])[] {
  if (typeof payload !== 'object' || payload === null) return []
  const raw = (payload as Record<string, unknown>)[field]
  if (!Array.isArray(raw)) return []
  const out: (readonly Cell[])[] = []
  for (const entry of raw) {
    const built = build(entry)
    if (built !== null) out.push(built)
  }
  return out
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
